/**
 * WebsitePortalButton — fixed corner link that opens the public Mi6 Group
 * website preview in a new tab.
 *
 * Sits to the left of the floating HelpButton so the two do not overlap.
 * Like HelpButton, it renders only when a session exists, and hides itself
 * on /pilot routes (the pilot header has its own slim chrome).
 *
 * The destination is the published restricted-launch preview at
 * https://mi6group-preview.pplx.app — the same URL the website itself uses
 * for its inverse "Attilius platform" corner link, so users can round-trip
 * between the platform and the website without leaving either context.
 */
import { useEffect, useState } from "react";
import { getSession } from "@/lib/session";

const WEBSITE_URL = "https://mi6group-preview.pplx.app";

export default function WebsitePortalButton() {
  const [hash, setHash] = useState<string>(() =>
    typeof window !== "undefined" ? window.location.hash : "",
  );
  const session = getSession();

  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (!session) return null;
  if (hash.startsWith("#/pilot")) return null;

  const style: React.CSSProperties = {
    // Sit to the LEFT of HelpButton. HelpButton is at right ~1.5rem and is
    // roughly 64px wide; offset this one by ~6.5rem to leave a gap.
    bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)",
    right: "calc(env(safe-area-inset-right, 0px) + 6.5rem)",
  };

  return (
    <a
      href={WEBSITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed z-40 whitespace-nowrap rounded-full bg-neutral-900 text-white px-4 py-2.5 shadow-lg hover:bg-neutral-800 text-[12px] font-medium transition-colors inline-flex items-center gap-2 no-underline"
      style={style}
      data-testid="website-portal-button"
      aria-label="Open the Mi6 Group website in a new tab"
      title="Open the Mi6 Group website"
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full bg-[#16A34A]"
      />
      <span>Mi6 Group website</span>
      <span aria-hidden="true" className="text-neutral-400 font-mono">
        →
      </span>
    </a>
  );
}
